// Copyright (c) 2023 WiseTime. All rights reserved.

import React, { useCallback, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useClickAway, useEvent } from "react-use"
import { darken } from "polished"
import { Trans } from "@lingui/react"
import styled, { css } from "styled-components/macro"
import * as TimelineSlice from "../slices/timeline"
import { PrimaryButton, SharedDropdownContent } from "../../shared/styles"
import * as TimelineActions from "../actions"
import { List } from "../../shared/search-dropdown/styles"
import AddToGroupDropdownListItem from "../shared/AddToGroupDropdownListItem"
import * as UserContextSlice from "../../user-context/slice"
import { useDropdownContentHandling } from "../../shared/search-dropdown/useDropdownContentHandling"
import { matchTimeGroupIdList, TimeGroupListItem } from "../timeGroups"
import { AddToTimeGroupRequest, TimeRow } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { isDisabledShortcut } from "../../../lib/utils"

const Wrapper = styled.div`
  position: relative;
`

const StyledPrimaryButton = styled(PrimaryButton)<{ isActive: boolean, isDisabled: boolean }>`
  position: relative;
  padding-left: 1rem;
  padding-right: 1rem;

  ${({ isActive, theme }) => isActive && css`
    background-color: ${({ theme }): string => darken(0.15, theme.colors.indigo)};
    border-color: ${({ theme }): string => darken(0.15, theme.colors.indigo)};
    
    &:after,
    &:before {
      content: "";
      border-style: solid;
      position: absolute;
      z-index: 101;
    }

    &:after {
      border-color: transparent transparent ${theme.colors.white} transparent;
      border-width: 6px;
      top: 25px;
      left: 50%;
      transform: translate3d(-50%, 0, 0);
    }

    &:before {
      border-color: transparent transparent rgba(193, 193, 193, .5) transparent;
      border-width: 7px;
      top: 23px;
      left: 50%;
      transform: translate3d(-50%, 0, 0);
    }
  `}
  
  ${({ isDisabled }) => isDisabled && css`
    opacity: .5;
    pointer-events: none;
  `}
`

const StyledList = styled(props => <List {...props} />)`
  && {
    ${SharedDropdownContent};
    border: 1px solid rgba(193, 193, 193, .5);
    position: absolute;
    top: 37px;
    left: -23px;
    width: 340px;
    max-width: calc(100vw - 40px);
    min-height: 0;
    max-height: 360px;
    z-index: 1;

    @media (min-width: ${({ theme }): string => theme.screen.xs}) {
      width: 365px;
    }

    @media (min-width: ${({ theme }): string => theme.screen.sm}) {
      min-height: 26px;
      max-height: calc(100vh - 291px);
      max-height: calc(100dvh - 291px);
    }

    @media (max-width: ${({ theme }): string => theme.screen.sm}) {
      top: 0;
      transform: translate3d(0, -100%, 0);
    }
  }
`

export const ArrowIcon = styled.span<{ isActive: boolean }>`
  display: inline-block;
  vertical-align: 2px;
  border-top: 4px solid ${({ theme }): string => theme.colors.white};
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  margin-left: 7px;

  ${({ isActive }) => isActive && css`
    transform: rotate(180deg);
  `}
`

type Props = {
  timeRows: TimeRow.AsObject[],
}

const AddToEntryDropdown = ({ timeRows }: Props) => {
  const dispatch = useDispatch()
  const hasTimeGroupsForDate = useSelector(TimelineSlice.selectHasTimeGroupsForDate)
  const timeGroups = useSelector(TimelineSlice.selectAvailableTimeGroups)
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const timeGroupsList: TimeGroupListItem[] = timeGroups.map(({
    id,
    name,
    narrative,
    tagsList,
    activityTypesList,
    tagCategoriesList,
  }) => ({
    id,
    label: name ?? "",
    narrative,
    tags: tagsList.filter(({ siteId }) => siteId === currentSiteId),
    activityTypes: activityTypesList.filter(({ siteId }) => siteId === currentSiteId),
    tagCategories: tagCategoriesList.filter(({ siteId }) => siteId === currentSiteId),
  }))
  const {
    activeIdIndex,
    setActiveIdIndex,
    activeId,
    setActiveId,
    handleArrowNavigation,
  } = useDropdownContentHandling()

  const onMouseMove = (): void => {
    if (activeIdIndex === undefined && activeId === undefined) {
      return
    }
    setActiveIdIndex(undefined)
    setActiveId(undefined)
  }

  const handleAddToGroup = (id: string) => {
    dispatch(TimelineActions.addToTimeGroup(
      new AddToTimeGroupRequest()
        .setTimeGroupId(id)
        .setTimeRowIdsList(timeRows.map(r => r.id))
    ))
    setIsDropdownVisible(false)
  }

  const onKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === "Escape" || e.key === "Tab") {
      setIsDropdownVisible(false)
    }
    if (e.key === "a" && timeGroupsList.length !== 0 && !isDisabledShortcut()) {
      setIsDropdownVisible(!isDropdownVisible)
    }
    if (activeId && (e.key === "Enter")) {
      handleAddToGroup(activeId)
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      handleArrowNavigation(
        "ArrowDown",
        timeGroupsList,
      )
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      handleArrowNavigation(
        "ArrowUp",
        timeGroupsList,
      )
    }
  }, [
    timeGroupsList,
  ])

  useEvent("keydown", onKeyDown)
  useClickAway(dropdownRef, () => setIsDropdownVisible(false))

  const matchedTimeGroupIdList = matchTimeGroupIdList(timeRows, timeGroupsList, currentSiteId)

  return (
    <Wrapper ref={dropdownRef}>
      <StyledPrimaryButton
        type="button"
        isDisabled={!hasTimeGroupsForDate}
        isActive={isDropdownVisible}
        onClick={() => {
          if (!hasTimeGroupsForDate) {
            return
          }
          setIsDropdownVisible(!isDropdownVisible)
        }}
      >
        <Trans id="label_add_to_entry" message="Add to Entry"/>
        {hasTimeGroupsForDate && <ArrowIcon isActive={isDropdownVisible}/>}
      </StyledPrimaryButton>
      {isDropdownVisible && hasTimeGroupsForDate && (
        <StyledList onMouseMove={onMouseMove}>
          {timeGroupsList.map(listItem => (
            <AddToGroupDropdownListItem
              key={listItem.id}
              listItem={listItem}
              activeId={activeId}
              isMatch={matchedTimeGroupIdList.includes(listItem.id)}
              handleAddToGroup={handleAddToGroup}
            />
          ))}
        </StyledList>
      )}
    </Wrapper>
  )
}

export default AddToEntryDropdown
